import React from 'react';
import { RouteObject } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import TreatmentPlan from 'pages/TreatmentPlan/TreatmentPlan';
import PatientRecords from 'pages/PatientRecords/PatientRecords';
import VisitChat from 'pages/VisitChat/VisitChat';
import VisitHistory from 'pages/VisitHistory/VisitHistory';
import PatientReferral from 'pages/PatientReferral/PatientReferral';
import ReferralDetails from 'pages/ReferralDetails/ReferralDetails';
import Layout, { loader as layoutLoader } from './components/Layout/Layout';
import Login, { action as loginAction, loader as loginLoader } from './pages/Login/Login';
import Home from './pages/Home/Home';
import Profile from './pages/Profile/Profile';
import VisitRecords from './components/VisitRecords/VisitRecords';
import About from './pages/About/About';
import MedicalVisits from './pages/MedicalVisits/MedicalVisits';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import Labs from './pages/Labs/Labs';
import MedicationHistory from './pages/MedicationHistory/MedicationHistory';
import VisitSymptoms from './pages/VisitSymptoms/VisitSymptoms';
import Notes from './pages/Notes/Notes';
import LabResult from './pages/LabResults/LabResults';
import VideoCall from './pages/VideoCall/VideoCall';
import MedicalHistory from './pages/MedicalHistory/MedicalHistory';

export const paths = {
  LOGIN_URL_PATH: '/login',
  HOME_URL_PATH: '/',
  PROFILE_URL_PATH: '/profile',
  PATIENT_URL_PATH: '/patient/:patientId',

  VISIT_RECORDS_URL_PATH: '/visit/:visitId', // '/visit/:visitId/medical-visits?type=""&patient=""'
  LAB_VISITS_URL_PATH: '/visit/:visitId/labs',
  MEDICATION_HISTORY_VISITS_URL_PATH: '/visit/:visitId/medication-history',
  SYMPTOMS_VISITS_URL_PATH: '/visit/:visitId/symptoms',
  NOTES_VISITS_URL_PATH: '/visit/:visitId/notes',
  MEDICAL_HISTORY_URL_PATH: '/visit/:visitId/medical-history',
  TREATMENT_PLAN_URL_PATH: '/visit/:visitId/treatment-plan',
  CHAT_URL_PATH: '/visit/:visitId/chat',
  VISIT_HISTORY_URL_PATH: '/visit/:visitId/visit-history',
  PATIENT_REFERRAL_TICKET_PATH: '/visit/:visitId/referral',
  REFERRAL_DETAILS_TICKET_PATH: '/visit/:visitId/referral-details',

  VISITS_URL_PATH: '/visits',
  VIDEO_CALL_URL_PATH: '/visit/video-call',
  LAB_RESULTS_PATH: '/lab/results/:labTestId',
};

const routes = (queryClient: QueryClient) =>
  [
    {
      path: paths.LOGIN_URL_PATH,
      element: <Login />,
      errorElement: <ErrorBoundary />,
      action: loginAction(),
      loader: loginLoader(),
    },
    {
      path: paths.HOME_URL_PATH,
      element: <Layout />,
      errorElement: <ErrorBoundary />,
      loader: layoutLoader(queryClient),
      children: [
        {
          errorElement: <ErrorBoundary />,
          children: [
            {
              index: true,
              element: <Home />,
            },
            {
              path: paths.PROFILE_URL_PATH,
              element: <Profile />,
            },
            {
              path: paths.PATIENT_URL_PATH,
              element: <PatientRecords />,
            },
            {
              path: paths.VISIT_RECORDS_URL_PATH,
              element: <VisitRecords />,
              children: [
                {
                  errorElement: <ErrorBoundary />,
                  children: [
                    {
                      index: true,
                      element: <About />,
                    },
                    {
                      path: paths.LAB_VISITS_URL_PATH,
                      element: <Labs />,
                    },
                    {
                      path: paths.MEDICATION_HISTORY_VISITS_URL_PATH,
                      element: <MedicationHistory />,
                    },
                    {
                      path: paths.SYMPTOMS_VISITS_URL_PATH,
                      element: <VisitSymptoms />,
                    },
                    {
                      path: paths.MEDICAL_HISTORY_URL_PATH,
                      element: <MedicalHistory />,
                    },
                    {
                      path: paths.TREATMENT_PLAN_URL_PATH,
                      element: <TreatmentPlan />,
                    },
                    {
                      path: paths.CHAT_URL_PATH,
                      element: <VisitChat />,
                    },
                    {
                      path: paths.NOTES_VISITS_URL_PATH,
                      element: <Notes />,
                    },
                    {
                      path: paths.VISIT_HISTORY_URL_PATH,
                      element: <VisitHistory />,
                    },
                  ],
                },
              ],
            },
            {
              path: paths.VISITS_URL_PATH,
              element: <MedicalVisits />,
            },
            {
              path: paths.LAB_RESULTS_PATH,
              element: <LabResult />,
            },
            {
              path: paths.VIDEO_CALL_URL_PATH,
              element: <VideoCall />,
            },
            {
              path: paths.PATIENT_REFERRAL_TICKET_PATH,
              element: <PatientReferral />,
            },
            {
              path: paths.REFERRAL_DETAILS_TICKET_PATH,
              element: <ReferralDetails />,
            },
          ],
        },
      ],
    },
  ] satisfies RouteObject[];

export default routes;
